import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ContributorMap } from "@/features/contributor-map";
import type { PublicHomeCopy } from "./public-home-copy";
import type { CollectionCountries, HomeFaqItem } from "./public-home-data";
import { HomeSection, PrimaryLink, SectionHeading, Waveform } from "./HomeParts";

interface CollectionMapProps {
  copy: PublicHomeCopy;
  places: CollectionCountries | null;
  isLoading: boolean;
}

/** The shared map, fed with where contributions were recorded (or profile countries as a fallback). */
export function CollectionMap({ copy, places, isLoading }: CollectionMapProps) {
  const c = copy.map;
  if ((!places && !isLoading) || places?.countries.length === 0) return null;
  const fromCollection = places?.source !== "profile";
  return (
    <HomeSection labelledBy="home-map-title">
      <SectionHeading id="home-map-title" eyebrow={c.eyebrow} title={c.title} />
      <div className="mt-10">
        <ContributorMap
          countries={places?.countries}
          countriesCount={places?.countries.length}
          isLoading={isLoading}
          labels={{
            title: fromCollection ? c.collectionTitle : c.profileTitle,
            subtitle: fromCollection ? c.collectionSubtitle : c.profileSubtitle,
            unit: fromCollection ? c.collectionUnit : c.profileUnit,
            countLabel: c.countLabel,
          }}
        />
      </div>
    </HomeSection>
  );
}

/** Published questions only; the section is not rendered when there are none. */
export function HomeFaq({ copy, items }: { copy: PublicHomeCopy; items: HomeFaqItem[] }) {
  if (items.length === 0) return null;
  const c = copy.faq;
  return (
    <HomeSection labelledBy="home-faq-title">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
        <SectionHeading id="home-faq-title" eyebrow={c.eyebrow} title={c.title} />
        <Accordion type="single" collapsible className="min-w-0 border-t border-border">
          {items.map((item) => (
            <AccordionItem key={item.id} value={String(item.id)} className="border-border">
              <AccordionTrigger className="gap-4 py-5 text-left text-base font-semibold text-foreground hover:no-underline focus-ring">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="whitespace-pre-line pr-8 text-[15px] leading-7 text-muted-foreground">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </HomeSection>
  );
}

export function FinalCta({ copy, href, label }: { copy: PublicHomeCopy; href: string; label: string }) {
  const c = copy.final;
  return (
    <section aria-labelledby="home-final-title" className="border-t border-border/60">
      <div className="container mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:py-24">
        <div className="grid grid-cols-1 items-center gap-10 rounded-3xl border border-border bg-surface p-6 sm:p-10 lg:grid-cols-[1.2fr_0.8fr] lg:p-14">
          <div>
            <h2 id="home-final-title" className="text-3xl font-extrabold leading-[1.08] tracking-[-0.035em] text-foreground sm:text-4xl lg:text-5xl">
              {c.title}
            </h2>
            <p className="mt-4 max-w-lg text-base leading-7 text-muted-foreground">{c.body}</p>
            <PrimaryLink to={href} className="mt-8 w-full sm:w-auto">
              {label}
            </PrimaryLink>
          </div>
          <Waveform className="hidden h-28 lg:flex" />
        </div>
      </div>
    </section>
  );
}
